import { TestOutcomes } from '@flakiness/server/common/stats/testOutcomes.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { humanReadableInteger, humanReadableMs } from '@flakiness/shared/common/utils.js';
import { consume, ContextType } from '@lit/context';
import * as d3 from 'd3';
import { css, html, LitElement, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { Temporal } from 'temporal-polyfill';
import { contexts } from '../contexts.js';
import { handleLink, Linkifier } from '../linkifier.js';
import { OUTCOME_LABELS, OUTCOME_ORDER, outcomeSwatchFill } from './outcome-legend.js';

export type DailyMetric = 'testsCount' | 'durationMs';

// Stacking runs bottom-up, so reversing severity puts passes at the base and
// regressions on the crest, where a spike is easiest to spot.
const STACK_ORDER: WireTypes.Outcome[] = OUTCOME_ORDER.toReversed();

const EMPTY_COUNTS: WireTypes.OutcomesCount = { regressed: 0, unexpected: 0, flaked: 0, skipped: 0, expected: 0 };

const TOOLTIP_PADDING = 6;
const TOOLTIP_HEADER_HEIGHT = 15;
const TOOLTIP_ROW_HEIGHT = 13;
const TOOLTIP_SWATCH = 8;
const TOOLTIP_LABEL_X = TOOLTIP_PADDING + TOOLTIP_SWATCH + 6;
const TOOLTIP_COLUMN_GAP = 14;

type Point = {
  day: Temporal.PlainDate,
  dayStr: string,
  value: number,
  counts: WireTypes.OutcomesCount,
};

type ChartFrame = {
  width: number,
  height: number,
  xScale: d3.ScalePoint<string>,
  yScale: d3.ScaleLinear<number, number>,
};

type Tooltip = {
  show: (point: Point) => void,
  hide: () => void,
};

type GSelection = d3.Selection<SVGGElement, unknown, null, undefined>;

@customElement('daily-metric-chart')
export class DailyMetricChart extends LitElement {
  @property({ attribute: false }) metric: DailyMetric = 'testsCount';
  /** Plotted when `metric` is `testsCount`. */
  @property({ attribute: false }) outcomes?: WireTypes.DailyOutcome[];
  /** Plotted when `metric` is `durationMs`. */
  @property({ attribute: false }) durations?: WireTypes.DailyDuration[];
  /**
   * Outcomes to plot as a stack. An empty selection plots the day total as a
   * single area, which is the default view.
   */
  @property({ attribute: false }) selectedOutcomes: WireTypes.Outcome[] = [];
  @property({ attribute: false }) selectedDay?: Temporal.PlainDate;
  @property({ attribute: false }) viewportSince?: Temporal.PlainDate;
  @property({ attribute: false }) viewportUntil?: Temporal.PlainDate;
  @property({ attribute: false }) linkifier?: Linkifier<Temporal.PlainDate>;

  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;

  @state() private _width: number = 0;
  @state() private _height: number = 0;

  @query('svg') private _svg?: SVGElement;
  private _resizeObserver?: ResizeObserver;

  override connectedCallback(): void {
    super.connectedCallback();
    this._resizeObserver = new ResizeObserver((entries) => {
      this._width = entries[0].contentRect.width;
      this._height = entries[0].contentRect.height;
      this._updateChart();
    });
    this._resizeObserver.observe(this);
  }

  override disconnectedCallback(): void {
    this._resizeObserver?.disconnect();
    this._resizeObserver = undefined;
    super.disconnectedCallback();
  }

  protected override updated(_changedProperties: PropertyValues): void {
    this._updateChart(); 
  }

  render() {
    return html`
      <svg></svg>
    `;
  }

  private _updateChart() {
    const svg = this._svg;
    if (!svg)
      return;

    const root = d3.select(svg);
    // Remove explicit size attrs before measuring so the SVG reflects host sizing.
    root.attr('width', null).attr('height', null);
    if (!this._width || !this._height)
      return;

    root.selectAll('*').remove();
    root.attr('width', this._width).attr('height', this._height);

    const metric = this.metric;
    // Durations carry no per-outcome breakdown, so a selection cannot apply to them.
    const stackKeys = metric === 'testsCount' ? STACK_ORDER.filter(outcome => this.selectedOutcomes.includes(outcome)) : [];
    // The y value is the selected subset when there is a selection, and the day
    // total otherwise. The wire only carries the breakdown, so both are summed here.
    const sumCounts = (counts: WireTypes.OutcomesCount) => stackKeys.length
      ? stackKeys.reduce((sum, outcome) => sum + counts[outcome], 0)
      : TestOutcomes.sumAllCounts(counts);
    const points: Point[] = metric === 'durationMs'
      ? (this.durations ?? []).toReversed()
        .filter(d => d.durationMs !== undefined)
        .map(d => ({
          day: Temporal.PlainDate.from(d.day),
          dayStr: d.day,
          value: d.durationMs!,
          counts: EMPTY_COUNTS,
        }))
      : (this.outcomes ?? []).toReversed()
        .filter(d => d.outcome !== 'idle' && d.outcome !== 'untested')
        .map(d => ({
          day: Temporal.PlainDate.from(d.day),
          dayStr: d.day,
          value: sumCounts(d.testStats ?? EMPTY_COUNTS),
          counts: d.testStats ?? EMPTY_COUNTS,
        }));

    const margin = { top: 12, right: 12, bottom: 20, left: 0 };

    const tickFormat = (d: d3.NumberValue) => metric === 'durationMs' ? humanReadableMs(d as number) : humanReadableInteger(d as number) + ' tests';

    // Compute left margin from tick label widths
    const yMaxRaw = d3.max(points, p => p.value) ?? 0;
    // A selected outcome that never occurs in the window is a state the legend
    // deliberately keeps reachable, so every point can legitimately be zero.
    // d3 collapses a [0, 0] domain onto the middle of the range, which would
    // float the baseline halfway up the plot, so fall back to the empty domain.
    const emptyMax = metric === 'durationMs' ? 1000 : 10;
    const yMax = points.length && yMaxRaw > 0 ? yMaxRaw * 1.15 : emptyMax;
    const tmpScale = d3.scaleLinear().domain([0, yMax]).nice();
    const tmpG = root.append('g').attr('font-size', '11px');
    tmpScale.ticks(4).forEach(t => tmpG.append('text').text(tickFormat(t)));
    margin.left = Math.ceil((tmpG.node() as SVGGElement).getBBox().width) + 12;
    tmpG.remove();

    const width = this._width - margin.left - margin.right;
    const height = this._height - margin.top - margin.bottom;
    if (width <= 0 || height <= 0)
      return;

    const xScale = d3.scalePoint<string>()
      .domain(points.map(p => p.dayStr))
      .range([0, width])
      .padding(0);

    const yScale = d3.scaleLinear()
      .domain([0, yMax])
      .range([height, 0])
      .nice();

    root.attr('width', this._width).attr('height', this._height);

    const g = root.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Y-axis grid lines
    g.append('g')
      .call(d3.axisLeft(yScale).ticks(4).tickFormat(tickFormat))
      .call(g => g.select('.domain').remove())
      .call(g => g.selectAll('.tick line')
        .attr('x2', width)
        .attr('stroke', 'var(--sl-color-neutral-200)')
        .attr('stroke-dasharray', '2,2')
      )
      .call(g => g.selectAll('.tick text')
        .attr('fill', 'var(--sl-color-neutral-500)')
        .attr('font-size', '11px')
      );

    // X-axis: month labels at month boundaries
    const monthLabels: { x: number, label: string }[] = [];
    let lastMonth = -1;
    for (const p of points) {
      if (p.day.month !== lastMonth) {
        lastMonth = p.day.month;
        monthLabels.push({
          x: xScale(p.dayStr)!,
          label: p.day.toLocaleString('en-US', { month: 'short' }),
        });
      }
    }
    g.selectAll('.month-label')
      .data(monthLabels)
      .join('text')
      .attr('x', d => d.x)
      .attr('y', height + 16)
      .attr('fill', 'var(--sl-color-neutral-500)')
      .attr('font-size', '11px')
      .text(d => d.label);

    // Area fill. With a selection this becomes one band per outcome, stacked
    // bottom-up in STACK_ORDER; without one it stays a single neutral area.
    if (stackKeys.length) {
      const bandArea = d3.area<{ point: Point, y0: number, y1: number }>()
        .x(d => xScale(d.point.dayStr)!)
        .y0(d => yScale(d.y0))
        .y1(d => yScale(d.y1))
        .curve(d3.curveMonotoneX);
      const baselines = new Map(points.map(point => [point, 0]));
      for (const outcome of stackKeys) {
        const band = points.map(point => {
          const y0 = baselines.get(point)!;
          const y1 = y0 + point.counts[outcome];
          baselines.set(point, y1);
          return { point, y0, y1 };
        });
        g.append('path')
          .datum(band)
          .attr('fill', outcomeSwatchFill(outcome))
          .attr('d', bandArea);
      }
    } else {
      const area = d3.area<typeof points[0]>()
        .x(d => xScale(d.dayStr)!)
        .y0(height)
        .y1(d => yScale(d.value))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(points)
        .attr('fill', 'var(--sl-color-primary-100)')
        .attr('d', area);
    }

    // Line
    const line = d3.line<typeof points[0]>()
      .x(d => xScale(d.dayStr)!)
      .y(d => yScale(d.value))
      .curve(d3.curveMonotoneX);

    // With bands carrying the color, a primary-blue crest reads as a sixth
    // series; neutral keeps it reading as the top of the stack.
    const lineColor = stackKeys.length ? 'var(--sl-color-neutral-500)' : 'var(--sl-color-primary-500)';
    const dotColor = stackKeys.length ? 'var(--sl-color-neutral-500)' : 'var(--sl-color-primary-500)';
    const selectedDotColor = stackKeys.length ? 'var(--sl-color-neutral-800)' : 'var(--sl-color-primary-700)';

    g.append('path')
      .datum(points)
      .attr('fill', 'none')
      .attr('stroke', lineColor)
      .attr('stroke-width', 1.5)
      .attr('d', line);

    // Dots
    const selectedDayStr = this.selectedDay?.toString();
    const router = this._router;
    const linkifier = this.linkifier;

    const dots = g.selectAll('.dot')
      .data(points)
      .join('circle')
      .attr('class', 'dot')
      .attr('cx', d => xScale(d.dayStr)!)
      .attr('cy', d => yScale(d.value))
      .attr('r', d => d.dayStr === selectedDayStr ? 5 : 3)
      .attr('fill', d => d.dayStr === selectedDayStr ? selectedDotColor : dotColor)
      .attr('stroke', 'white')
      .attr('stroke-width', d => d.dayStr === selectedDayStr ? 2 : 1.5);

    const frame: ChartFrame = { width, height, xScale, yScale };

    // Durations have no per-outcome breakdown, so they keep the compact one-liner.
    const showsBreakdown = metric === 'testsCount';
    const guide = showsBreakdown ? this._appendGuideLine(g, frame) : undefined;
    const tooltip = showsBreakdown
      ? this._createBreakdownTooltip(g, frame, stackKeys)
      : this._createValueTooltip(g, frame, metric);

    // Vertical hit bands — full-height strips for each data point
    const step = xScale.step();
    g.selectAll('.hit-band')
      .data(points)
      .join('rect')
      .attr('class', 'hit-band')
      .attr('x', d => xScale(d.dayStr)! - step / 2)
      .attr('y', 0)
      .attr('width', step)
      .attr('height', height)
      .attr('fill', 'transparent')
      .attr('cursor', 'pointer')
      .on('mouseenter', (_event: MouseEvent, d: Point) => {
        dots.filter(p => p === d)
          .attr('r', 6)
          .attr('fill', selectedDotColor)
          .attr('stroke-width', 2);
        guide?.show(xScale(d.dayStr)!);
        tooltip.show(d);
      })
      .on('mouseleave', (_event: MouseEvent, d: Point) => {
        dots.filter(p => p === d)
          .attr('r', d.dayStr === selectedDayStr ? 5 : 3)
          .attr('fill', d.dayStr === selectedDayStr ? selectedDotColor : dotColor)
          .attr('stroke-width', d.dayStr === selectedDayStr ? 2 : 1.5);
        guide?.hide();
        tooltip.hide();
      })
      .on('click', (_event: MouseEvent, d: Point) => {
        handleLink(router, linkifier?.(d.day));
      });
  }

  private _appendGuideLine(g: GSelection, frame: ChartFrame) {
    const line = g.append('line')
      .attr('y1', 0)
      .attr('y2', frame.height)
      .attr('stroke', 'var(--sl-color-neutral-500)')
      .attr('stroke-dasharray', '2,2')
      .style('display', 'none')
      .style('pointer-events', 'none');
    return {
      show: (x: number) => line.attr('x1', x).attr('x2', x).style('display', null),
      hide: () => line.style('display', 'none'),
    };
  }

  // Single-line tooltip: date and the raw metric value.
  private _createValueTooltip(g: GSelection, frame: ChartFrame, metric: DailyMetric): Tooltip {
    const { width, xScale, yScale } = frame;
    const tooltip = g.append('g').attr('class', 'tooltip').style('display', 'none').style('pointer-events', 'none');
    const rect = tooltip.append('rect')
      .attr('rx', 4).attr('ry', 4)
      .attr('fill', 'var(--sl-color-neutral-800)')
      .attr('height', 20);
    const text = tooltip.append('text')
      .attr('fill', 'white')
      .attr('font-size', '11px')
      .attr('dy', 14)
      .attr('dx', 6);

    return {
      show: (point: Point) => {
        // We want to show time in proper units, and tests as a full number.
        const valueLabel = metric === 'durationMs' ? humanReadableMs(point.value) : `${point.value.toLocaleString()} tests`;
        text.text(`${point.day.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} — ${valueLabel}`);
        const textWidth = (text.node() as SVGTextElement).getComputedTextLength();
        rect.attr('width', textWidth + 12);

        let tx = xScale(point.dayStr)! - (textWidth + 12) / 2;
        tx = Math.max(0, Math.min(tx, width - textWidth - 12));
        const ty = yScale(point.value) - 28;
        tooltip.attr('transform', `translate(${tx},${ty})`).style('display', null);
      },
      hide: () => tooltip.style('display', 'none'),
    };
  }

  // Multi-line tooltip: date, the plotted value, and one row per outcome. With a
  // selection the rows are exactly the selected outcomes, zeroes included, since
  // an explicitly picked series reading 0 is itself the answer.
  private _createBreakdownTooltip(g: GSelection, frame: ChartFrame, stackKeys: WireTypes.Outcome[]): Tooltip {
    const { width, height, xScale, yScale } = frame;
    const tooltip = g.append('g').attr('class', 'tooltip').style('display', 'none').style('pointer-events', 'none');
    const rect = tooltip.append('rect')
      .attr('rx', 4).attr('ry', 4)
      .attr('fill', 'var(--sl-color-neutral-800)');
    const body = tooltip.append('g');

    return {
      show: (point: Point) => {
        body.selectAll('*').remove();
        const rows = stackKeys.length
          ? OUTCOME_ORDER.filter(outcome => stackKeys.includes(outcome))
          : OUTCOME_ORDER.filter(outcome => point.counts[outcome] > 0);

        const header = body.append('text')
          .attr('x', TOOLTIP_PADDING)
          .attr('y', TOOLTIP_PADDING + 11)
          .attr('fill', 'white')
          .attr('font-size', '11px')
          .attr('font-weight', '600')
          .text(`${point.day.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} — ${point.value.toLocaleString()} tests`);

        let contentWidth = header.node()!.getComputedTextLength();
        const counts: SVGTextElement[] = [];
        rows.forEach((outcome, index) => {
          const baseline = TOOLTIP_PADDING + TOOLTIP_HEADER_HEIGHT + index * TOOLTIP_ROW_HEIGHT + 10;
          body.append('rect')
            .attr('x', TOOLTIP_PADDING)
            .attr('y', baseline - TOOLTIP_SWATCH)
            .attr('width', TOOLTIP_SWATCH)
            .attr('height', TOOLTIP_SWATCH)
            .attr('rx', 1)
            .style('fill', outcomeSwatchFill(outcome));
          const label = body.append('text')
            .attr('x', TOOLTIP_LABEL_X)
            .attr('y', baseline)
            .attr('fill', 'var(--sl-color-neutral-300)')
            .attr('font-size', '11px')
            .text(OUTCOME_LABELS[outcome]);
          const count = body.append('text')
            .attr('y', baseline)
            .attr('fill', 'white')
            .attr('font-size', '11px')
            .attr('text-anchor', 'end')
            .text(point.counts[outcome].toLocaleString());
          const rowWidth = (TOOLTIP_LABEL_X - TOOLTIP_PADDING) + label.node()!.getComputedTextLength()
            + TOOLTIP_COLUMN_GAP + count.node()!.getComputedTextLength();
          contentWidth = Math.max(contentWidth, rowWidth);
          counts.push(count.node()!);
        });

        const tooltipWidth = contentWidth + TOOLTIP_PADDING * 2;
        const tooltipHeight = TOOLTIP_PADDING * 2 + TOOLTIP_HEADER_HEIGHT + rows.length * TOOLTIP_ROW_HEIGHT;
        for (const count of counts)
          count.setAttribute('x', String(tooltipWidth - TOOLTIP_PADDING));
        rect.attr('width', tooltipWidth).attr('height', tooltipHeight);

        // The tooltip is nearly as tall as the plot, so keep it off the hovered
        // column: flip to whichever side has room.
        const x = xScale(point.dayStr)!;
        let tx = x > width / 2 ? x - tooltipWidth - 12 : x + 12;
        tx = Math.max(0, Math.min(tx, Math.max(0, width - tooltipWidth)));
        const ty = Math.max(0, Math.min(yScale(point.value) - tooltipHeight - 10, height - tooltipHeight));
        tooltip.attr('transform', `translate(${tx},${ty})`).style('display', null);
      },
      hide: () => tooltip.style('display', 'none'),
    };
  }

  static styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      flex: auto;
      align-self: stretch;
      min-width: 0;
      min-height: 0;
    }
    svg {
      display: block;
      width: 100%;
      height: 100%;
    }
  `;
}
